import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  askSectionExpert,
  generateSectionFigure,
  type SectionFigure,
  type SectionQATurn,
} from "@/api/projects";
import { generationErrorMessage } from "@/api/llm-access";
import { Button } from "@/components/ui";
import { MathText } from "@/components/math-text";

/**
 * Per-section "Ask the expert" — a free-form Q&A panel mounted under each
 * result topic section. Answers come from an LLM grounded in the compressed
 * whole-summary + this section's full text + the project's field persona. Turns
 * are persisted server-side, so on success we invalidate the project query to
 * re-hydrate the thread from `topic_section.expert_thread`.
 *
 * An image-mode toggle reuses the same box: the expert turns the description
 * into a gpt-image-1 prompt and the rendered image joins the project figure
 * library, folded back here as `topic_section.generated_figures`.
 */
export function SectionExpertPanel({
  projectId,
  slug,
  label,
  thread,
  figures,
}: {
  projectId: string;
  slug: string;
  label: string;
  thread?: SectionQATurn[];
  figures?: SectionFigure[];
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [draft, setDraft] = useState("");
  const [imageMode, setImageMode] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onSuccess = () => {
    setDraft("");
    setError(null);
    void qc.invalidateQueries({ queryKey: ["project", projectId] });
  };

  const ask = useMutation({
    mutationFn: (question: string) => askSectionExpert(projectId, slug, { question }),
    onSuccess,
    onError: (e) => setError(generationErrorMessage(e, t, t("Couldn't ask the expert."))),
  });
  const visualize = useMutation({
    mutationFn: (demand: string) => generateSectionFigure(projectId, slug, { demand }),
    onSuccess,
    onError: (e) => setError(generationErrorMessage(e, t, t("Couldn't generate the image."))),
  });

  const turns = thread ?? [];
  const imgs = figures ?? [];
  const trimmed = draft.trim();
  const pending = ask.isPending || visualize.isPending;

  const submit = () => {
    if (!trimmed || pending) return;
    if (imageMode) visualize.mutate(trimmed);
    else ask.mutate(trimmed);
  };

  return (
    <details className="mt-3 rounded-lg border border-black/10 bg-brand-50/40 p-3">
      <summary className="cursor-pointer select-none text-sm font-semibold text-ink">
        {t("Ask the expert")}
        {turns.length > 0 && <span className="ml-1 text-ink-faint">({turns.length})</span>}
      </summary>

      <div className="mt-3 space-y-3">
        {turns.map((turn) => (
          <div key={turn.id} className="space-y-1">
            <p className="text-sm font-medium text-ink">
              <span className="text-ink-faint">{t("You")}: </span>
              <MathText text={turn.question} />
            </p>
            <div className="rounded-md bg-white px-3 py-2 text-sm text-ink-soft">
              <MathText text={turn.answer} />
            </div>
          </div>
        ))}

        {imgs.map((fig) => (
          <figure key={fig.id} className="space-y-1">
            {fig.url && (
              <img
                src={fig.url}
                alt={fig.caption ?? ""}
                className="w-full rounded-md border border-black/10 bg-white"
              />
            )}
            {fig.caption && (
              <figcaption className="text-xs text-ink-faint">{fig.caption}</figcaption>
            )}
          </figure>
        ))}

        <div className="space-y-2">
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if ((e.metaKey || e.ctrlKey) && e.key === "Enter") submit();
            }}
            rows={2}
            placeholder={
              imageMode
                ? t("Describe the image you want…")
                : t('Ask about "{{label}}"…', { label })
            }
            className="w-full resize-y rounded-md border border-black/10 px-3 py-2 text-sm text-ink placeholder:text-ink-faint focus:border-brand-400 focus:outline-none"
            disabled={pending}
          />
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setImageMode((v) => !v)}
                disabled={pending}
                className={`rounded-md border px-2 py-1 text-xs ${
                  imageMode
                    ? "border-brand-400 bg-brand-100 text-brand-700"
                    : "border-black/10 text-ink-faint"
                }`}
                title={t("Generate an image instead")}
              >
                🖼 {t("Image")}
              </button>
              {error ? (
                <span className="text-xs text-red-500">{error}</span>
              ) : (
                <span className="text-xs text-ink-faint">
                  {imageMode
                    ? t("The expert draws it. ~$0.04 per image.")
                    : t("Answers are specific to this section.")}
                </span>
              )}
            </div>
            <Button onClick={submit} disabled={!trimmed || pending}>
              {imageMode
                ? visualize.isPending
                  ? t("Generating…")
                  : t("Generate")
                : ask.isPending
                  ? t("Asking…")
                  : t("Ask")}
            </Button>
          </div>
        </div>
      </div>
    </details>
  );
}
